// OKLCH color math — hand-rolled to avoid a dependency. Standard sRGB→OKLab
// pipeline (Bjorn Ottosson's reference constants), cylindrical form for hue.
// Consumers: tenantPalette (brand-slot derivation), paletteChecks (validator).

export interface Oklch {
  l: number
  c: number
  h: number // degrees [0, 360)
}

function srgbToLinear(u: number): number {
  return u <= 0.04045 ? u / 12.92 : Math.pow((u + 0.055) / 1.055, 2.4)
}

function linearToSrgb(u: number): number {
  return u <= 0.0031308 ? 12.92 * u : 1.055 * Math.pow(u, 1 / 2.4) - 0.055
}

function hexToRgb01(hex: string): [number, number, number] {
  const m = /^#([0-9a-fA-F]{6})$/.exec(hex)
  if (!m) throw new Error(`oklch: not a #rrggbb hex: "${hex}"`)
  const n = parseInt(m[1]!, 16)
  return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255]
}

function oklabFromLinear(r: number, g: number, b: number): [number, number, number] {
  const l = 0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b
  const m = 0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b
  const s = 0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b
  const l_ = Math.cbrt(l)
  const m_ = Math.cbrt(m)
  const s_ = Math.cbrt(s)
  return [
    0.2104542553 * l_ + 0.793617785 * m_ - 0.0040720468 * s_,
    1.9779984951 * l_ - 2.428592205 * m_ + 0.4505937099 * s_,
    0.0259040371 * l_ + 0.7827717662 * m_ - 0.808675766 * s_,
  ]
}

function linearFromOklab(L: number, a: number, b: number): [number, number, number] {
  const l_ = L + 0.3963377774 * a + 0.2158037573 * b
  const m_ = L - 0.1055613458 * a - 0.0638541728 * b
  const s_ = L - 0.0894841775 * a - 1.291485548 * b
  const l = l_ * l_ * l_
  const m = m_ * m_ * m_
  const s = s_ * s_ * s_
  return [
    +4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
    -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
    -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s,
  ]
}

export function hexToOklch(hex: string): Oklch {
  const [r, g, b] = hexToRgb01(hex)
  const [L, a, bb] = oklabFromLinear(srgbToLinear(r), srgbToLinear(g), srgbToLinear(b))
  const c = Math.sqrt(a * a + bb * bb)
  let h = (Math.atan2(bb, a) * 180) / Math.PI
  if (h < 0) h += 360
  return { l: L, c, h }
}

function tryOklchToRgb01(l: number, c: number, h: number): [number, number, number] | null {
  const hr = (h * Math.PI) / 180
  const [lr, lg, lb] = linearFromOklab(l, c * Math.cos(hr), c * Math.sin(hr))
  const r = linearToSrgb(lr)
  const g = linearToSrgb(lg)
  const b = linearToSrgb(lb)
  const eps = 1e-4
  if (r < -eps || r > 1 + eps || g < -eps || g > 1 + eps || b < -eps || b > 1 + eps) return null
  return [Math.min(1, Math.max(0, r)), Math.min(1, Math.max(0, g)), Math.min(1, Math.max(0, b))]
}

/** OKLCH → hex. Out-of-gamut chroma is reduced (binary search) until sRGB fits —
 *  the standard "keep L and H, sacrifice C" gamut mapping. */
export function oklchToHex(l: number, c: number, h: number): string {
  let rgb = tryOklchToRgb01(l, c, h)
  if (!rgb) {
    let lo = 0
    let hi = c
    for (let i = 0; i < 24; i++) {
      const mid = (lo + hi) / 2
      if (tryOklchToRgb01(l, mid, h)) lo = mid
      else hi = mid
    }
    rgb = tryOklchToRgb01(l, lo, h) ?? [0, 0, 0]
  }
  const to255 = (u: number) =>
    Math.round(u * 255)
      .toString(16)
      .padStart(2, '0')
  return `#${to255(rgb[0])}${to255(rgb[1])}${to255(rgb[2])}`
}

export function hueDistanceDeg(a: number, b: number): number {
  const d = Math.abs(a - b) % 360
  return d > 180 ? 360 - d : d
}

/** Snap a color into the categorical band (spec §3.2): L→[0.43,0.75], C≥0.10, hue kept.
 *  Chroma may then be gamut-reduced by oklchToHex — the tests allow small drift. */
export function snapIntoBand(hex: string): string {
  const { l, c, h } = hexToOklch(hex)
  const l2 = Math.min(0.75, Math.max(0.43, l))
  const c2 = Math.max(0.1, c)
  return oklchToHex(l2, c2, h)
}
